/**
 * @file Agent 相关 API 接口定义
 * 对应 api文档.md 中 5.1 ~ 5.9 全部接口
 * 仅负责请求/响应的封装，不包含业务逻辑
 */
import axios from '@utils/axios';
import axiosBase from 'axios';

const BASE = '/lbsmap/portal/openchat/v1';
const jsonHeaders = { 'Content-Type': 'application/json' };

function agentHeaders(agentId: string) {
    return { ...jsonHeaders, 'X-Agent-ID': agentId };
}

// ============================================================================
// 5.1 发送消息
// ============================================================================

export interface HitlResponse {
    action: string;
    data: Record<string, unknown>;
}

export interface ChatParams {
    agentId: string;
    userQuery: string;
    sessionId?: string;
    turnId?: string;
    filesInput?: Array<{ file_name: string }>;
    /** 上传文件的 BOS file_keys（已上传后返回） */
    fileKeys?: string[];
    /** 上传文件的最小元数据，用于服务端展示和审计 */
    fileInfos?: Array<{ file_key: string; original_name: string }>;
    hitlResponse?: HitlResponse;
    subagent?: string;
    /** 推广链接来源标识，仅新会话首轮传递 */
    source?: string;
    /** 平台标识 */
    platform?: string;
}

export interface ChatResult {
    session_id: string;
    turn_id: string;
}

export async function chatApi(params: ChatParams): Promise<ChatResult> {
    const body: Record<string, unknown> = {
        user_query: params.userQuery,
        params: {
            ...(params.sessionId ? { session_id: params.sessionId } : {}),
            ...(params.turnId ? { turn_id: params.turnId } : {}),
            ...(params.hitlResponse ? { hitl_response: params.hitlResponse } : {}),
            ...(params.subagent ? { subagent: params.subagent } : {}),
            ...(params.source ? { source: params.source } : {}),
            ...(params.platform ? { platform: params.platform } : {}),
        },
    };
    if (params.filesInput && params.filesInput.length > 0) {
        body.files_input = params.filesInput;
    }
    if (params.fileKeys && params.fileKeys.length > 0) {
        body.file_keys = params.fileKeys;
    }
    if (params.fileInfos && params.fileInfos.length > 0) {
        body.file_infos = params.fileInfos;
    }
    try {
        const res: any = await axios.post(`${BASE}/chat`, body, {
            headers: agentHeaders(params.agentId),
        });
        // 业务错误：status !== 0
        if (res?.status !== 0) {
            throw new Error(res?.message || '服务请求异常，请稍后重试');
        }
        return res?.data;
    } catch (error: unknown) {
        // 已经是 Error 实例（含上面手动 throw 的）直接透传
        if (error instanceof Error && !(axiosBase.isAxiosError(error))) {
            throw error;
        }
        if (axiosBase.isAxiosError(error)) {
            // 超时
            if (error.code === 'ECONNABORTED' || error.code === 'ERR_CANCELED') {
                throw new Error('服务请求异常，请稍后重试');
            }
            // HTTP 错误响应里的 message
            const msg = (error.response?.data as any)?.message;
            if (msg) throw new Error(msg);
        }
        throw new Error('服务请求异常，请稍后重试');
    }
}

// ============================================================================
// 5.2 获取流式消息 (SSE)
// 注意: SSE 需要自定义 header (X-Agent-ID)，EventSource 不支持自定义 header，
// 因此流式连接由 SDK 层使用 fetch API 实现，此处仅导出 URL 构建工具
// ============================================================================

export function buildStreamUrl(params: {
    agentId: string;
    sessionId: string;
    turnId: string;
    fromId?: string;
}): string {
    const qs = new URLSearchParams({
        session_id: params.sessionId,
        turn_id: params.turnId,
        from_id: params.fromId || '0-0',
    });
    return `${BASE}/stream?${qs.toString()}`;
}

export function buildStreamHeaders(agentId: string): Record<string, string> {
    return agentHeaders(agentId);
}

// ============================================================================
// 5.3 获取会话列表
// ============================================================================

export interface SessionItem {
    session_id: string;
    title: string;
    status: string;
    created_at: string;
    updated_at: string;
    message_count: number;
    last_message_preview: string;
}

export interface SessionListResult {
    sessions: SessionItem[];
    total: number;
    limit: number;
    offset: number;
}

export async function getSessionsApi(params: {
    agentId: string;
    offset?: number;
    limit?: number;
}): Promise<SessionListResult> {
    const res: any = await axios.get(`${BASE}/session`, {
        params: { offset: params.offset ?? 0, limit: params.limit ?? 20 },
        headers: agentHeaders(params.agentId),
    });
    return res?.data;
}

// ============================================================================
// 5.4 修改会话信息
// ============================================================================

export async function updateSessionApi(params: {
    agentId: string;
    sessionId: string;
    title: string;
}): Promise<any> {
    const res: any = await axios.patch(
        `${BASE}/session`,
        { session_id: params.sessionId, title: params.title },
        { headers: agentHeaders(params.agentId) },
    );
    return res?.data;
}

// ============================================================================
// 5.5 删除会话信息
// ============================================================================

export async function deleteSessionApi(params: {
    agentId: string;
    sessionId: string;
}): Promise<any> {
    const res: any = await axios.delete(`${BASE}/session`, {
        data: { session_id: params.sessionId },
        headers: agentHeaders(params.agentId),
    });
    return res?.data;
}

// ============================================================================
// 5.6 获取消息信息
// ============================================================================

export interface MessageItem {
    message_id: string;
    turn_id: string;
    role: 'user' | 'assistant' | 'control' | 'terminal';
    content: string;
    content_type: string;
    label: string | null;
    created_at: string;
    /** 服务端可能返回的用户消息附件（兼容字段） */
    attachments?: Array<{
        file_key: string;
        original_name: string;
        size?: number;
        mime?: string;
        content_type?: string;
        download_url?: string;
    }>;
    file_infos?: Array<{
        file_key: string;
        original_name: string;
    }>;
}

export interface MessagesResult {
    messages: MessageItem[];
    status: string;
    current_turn: {
        turn_id: string;
        last_cursor: string;
    } | null;
    feedback: Record<string, any> | null;
}

export async function getMessagesApi(params: {
    agentId: string;
    sessionId: string;
}): Promise<MessagesResult> {
    const res: any = await axios.get(`${BASE}/message`, {
        params: { session_id: params.sessionId },
        headers: agentHeaders(params.agentId),
    });
    return res?.data;
}

// ============================================================================
// 5.7 会话消息取消
// ============================================================================

export async function cancelMessageApi(params: {
    agentId: string;
    sessionId: string;
    turnId: string;
}): Promise<any> {
    const res: any = await axios.delete(`${BASE}/message`, {
        data: { session_id: params.sessionId, turn_id: params.turnId },
        headers: agentHeaders(params.agentId),
    });
    return res?.data;
}

// ============================================================================
// 5.8 消息反馈
// ============================================================================

export async function feedbackApi(params: {
    agentId: string;
    sessionId: string;
    turnId: string;
    feedbackType: 'like' | 'dislike' | null;
    detail?: { reasons?: string; comment?: string };
}): Promise<any> {
    const res: any = await axios.post(
        `${BASE}/feedback`,
        {
            session_id: params.sessionId,
            turn_id: params.turnId,
            feedback_type: params.feedbackType,
            detail: params.detail || {},
        },
        { headers: agentHeaders(params.agentId) },
    );
    return res?.data;
}

// ============================================================================
// 5.9 获取智能体
// ============================================================================

export interface AgentItem {
    id: string;
    title: string;
    description: string;
    version: string;
    conf: {
        enable_file?: boolean;
    };
}

export interface AgentListResult {
    agents: AgentItem[];
    total: number;
}

export async function getAgentsApi(params?: {
    offset?: number;
    limit?: number;
}): Promise<AgentListResult> {
    const res: any = await axios.get(`${BASE}/agent`, {
        params: { offset: params?.offset ?? 0, limit: params?.limit ?? 20 },
        headers: jsonHeaders,
    });
    return res?.data;
}

// ============================================================================
// 5.11 搜索消息的会话列表
// ============================================================================

/** 搜索接口返回的单条命中轮次记录 */
export interface MessageSearchItem {
    turn_id: string;
    session_id: string;
    user_message_content: string;       // 用户提问，作为标题展示
    assistant_message_content: string;  // 助手回复，作为内容预览展示
    created_at: string;
    updated_at: string;
}

export interface MessageSearchResult {
    turns: MessageSearchItem[];
    total: number;
    limit: number;
    offset: number;
}

export async function searchByMessageApi(params: {
    agentId: string;
    keyword: string;
    offset?: number;
    limit?: number;
}): Promise<MessageSearchResult> {
    const res: any = await axios.get(`${BASE}/search-by-message`, {
        params: { keyword: params.keyword, offset: params.offset ?? 0, limit: params.limit ?? 20 },
        headers: agentHeaders(params.agentId),
    });
    return res?.data;
}

// ============================================================================
// 5.13 代码 Run
// ============================================================================

export interface CodeCardRunResult {
    success: boolean;
    stdout: string;
    stderr: string;
    exit_code: number;
}

export async function codeCardRunApi(params: {
    agentId: string;
    session_id: string;
    card_id: string;
}): Promise<CodeCardRunResult> {
    const res: any = await axios.post(`${BASE}/code-card/run`, {
        session_id: params.session_id,
        card_id: params.card_id,
        code: '',
        // inject_baidu_map_ak: true,
    }, {
        headers: agentHeaders(params.agentId),
    });
    return res?.data;
}

// ============================================================================
// 5.13 获取建议问题列表
// ============================================================================

export interface ProblemSuggestions {
    coding_agent: string[];
    mapya: string[];
}

export async function getProblemSuggestionsApi(): Promise<ProblemSuggestions> {
    const res: any = await axios.get(`${BASE}/problem`);
    return res?.problem ?? { coding_agent: [], mapya: [] };
}

// ============================================================================
// 5.12 校验用户权限
// ============================================================================

export interface WhitelistCheckResult {
    /** 用户拥有的权限列表，如 ["mapya", "coding_agent"] */
    permissions: string[];
}

export async function checkWhitelistApi(agentId: string): Promise<WhitelistCheckResult> {
    const res: any = await axios.post(`/lbsmap/portal/whitelist/check`, {}, {
        headers: agentHeaders(agentId),
    });
    // res 已被 axios interceptor 解为 response.data = { data: [...], message, status }
    const data = res?.data;
    return { permissions: Array.isArray(data) ? data : [] };
}
